import { Progress, parseUtc } from "../../features/lifecycle/api";

export interface BillingLine {
  attention: boolean;
  text: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const daysUntil = (date: Date, now: Date): number =>
  Math.max(0, Math.ceil((date.getTime() - now.getTime()) / DAY_MS));

const dayCount = (days: number): string =>
  days === 1 ? "1 day" : `${days} days`;

const shortDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

function charge(progress: Progress): string | null {
  if (progress.billing_amount_cents == null) return null;
  const amount = `$${(progress.billing_amount_cents / 100).toFixed(2)}`;
  if (progress.billing_interval === "year") return `${amount} a year`;
  if (progress.billing_interval === "month") return `${amount} a month`;
  return amount;
}

/**
 * The one line that answers what am I paying and when. Null when there is
 * nothing certain to say: a status mid-update must not show a stale date.
 */
export function billingStripLine(
  progress: Progress,
  now: Date = new Date(),
): BillingLine | null {
  if (progress.billing_pending) return null;
  const status = progress.billing_status ?? "";
  if (["past_due", "unpaid", "incomplete"].includes(status)) {
    return {
      attention: true,
      text: "Your payment needs attention. Update your card to keep your access.",
    };
  }
  if (progress.collection_paused) return null;
  const plan = progress.plan ?? "Your";

  if (status === "trialing") {
    const end = parseUtc(progress.trial_end);
    if (!end) return null;
    const days = daysUntil(end, now);
    const left = days === 0 ? "ends today" : `${dayCount(days)} left`;
    if (progress.canceling) {
      return {
        attention: false,
        text: `${plan} trial, ${left}. Renewal is off, so you will not be charged.`,
      };
    }
    const amount = charge(progress);
    return {
      attention: false,
      text: `${plan} trial, ${left}. First charge ${shortDate(end)}${amount ? `, ${amount}` : ""}.`,
    };
  }

  if (status === "active") {
    const end = parseUtc(progress.period_end);
    if (progress.canceling) {
      return end
        ? { attention: false, text: `Renewal is off. Your access runs through ${shortDate(end)}.` }
        : null;
    }
    if (!end) return null;
    const amount = charge(progress);
    const next = `Next charge ${shortDate(end)}${amount ? `, ${amount}` : ""}.`;
    const refundEnd = parseUtc(progress.refund_window_end);
    if (refundEnd && refundEnd.getTime() > now.getTime()) {
      return {
        attention: false,
        text: `${next} ${dayCount(daysUntil(refundEnd, now))} left in your refund window.`,
      };
    }
    return { attention: false, text: next };
  }
  return null;
}

/** The promise on the post-checkout page, in the same words as the strip. */
export function welcomeChargeLine(
  progress: Progress,
  now: Date = new Date(),
): string | null {
  if (progress.billing_pending) return null;
  const amount = charge(progress);
  if (progress.billing_status === "trialing") {
    const end = parseUtc(progress.trial_end);
    if (!end) return null;
    const days = daysUntil(end, now);
    const first = `First charge ${shortDate(end)}${amount ? `, ${amount}` : ""}.`;
    return progress.canceling
      ? `Your ${days} day trial is running with renewal off, so you will not be charged.`
      : `Your ${days} day trial is running. Nothing is charged today. ${first} Cancel before then and you pay nothing.`;
  }
  if (progress.billing_status === "active") {
    const end = parseUtc(progress.period_end);
    const refundEnd = parseUtc(progress.refund_window_end);
    const next = end
      ? `Next charge ${shortDate(end)}${amount ? `, ${amount}` : ""}.`
      : "";
    const refund =
      refundEnd && refundEnd.getTime() > now.getTime()
        ? ` You have ${dayCount(daysUntil(refundEnd, now))} to ask for a refund.`
        : "";
    return `${next}${refund}`.trim() || null;
  }
  return null;
}

/** Boards and tools only. Marketing and content pages never carry billing chrome. */
const PRODUCT_PATHS = [
  "/positive-ev",
  "/arbitrage",
  "/middles",
  "/sharp-money",
  "/parlay-builder",
  "/prediction-traders",
  "/my-bets",
];

export function isProductPath(pathname: string): boolean {
  if (pathname.includes("/share/")) return false;
  return PRODUCT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}
